/**
 * Hook para buscar resumo de status dos equipamentos
 * 
 * Utiliza React Query para cache e gerenciamento de estado assíncrono.
 * Retorna dados formatados para gráfico de rosca.
 * 
 * @module hooks/data/useEquipmentStatusSummary
 */

import { useQuery } from '@tanstack/react-query';
import api from '../../api/api';
import { logHttpError } from '../../utils/errorLogger';
import { getErrorMessage } from '../../utils/errorMessages';

// Query key para cache do React Query
export const EQUIPMENT_STATUS_SUMMARY_QUERY_KEY = ['dashboard', 'equipment-status-summary'];

/**
 * Hook para buscar resumo de status dos equipamentos
 * 
 * Configurações do React Query:
 * - staleTime: 30 minutos (dados considerados frescos)
 * - gcTime: 10 minutos (tempo de cache após desmontagem)
 * - retry: 3 tentativas com backoff exponencial
 * 
 * @returns {object} Objeto com dados, estados e funções
 * @property {Array} data - Array de objetos com status e total
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {string|null} error - Mensagem de erro (null se não houver erro)
 * @property {function} refetch - Função para recarregar os dados manualmente
 * 
 * @example
 * ```javascript
 * const { data, isLoading, error } = useEquipmentStatusSummary();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <DonutChart data={data} />;
 * ```
 */
export const useEquipmentStatusSummary = () => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: EQUIPMENT_STATUS_SUMMARY_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await api.get('/dashboard/equipment-status-summary');
                // Garante que retorna um array válido
                const result = response.data?.data || response.data || [];
                return Array.isArray(result) ? result : [];
            } catch (err) {
                logHttpError(err, {
                    component: 'useEquipmentStatusSummary',
                    endpoint: '/dashboard/equipment-status-summary',
                });
                throw err;
            }
        },
        staleTime: 1000 * 60 * 30, // 30 minutos - dados considerados frescos
        gcTime: 1000 * 60 * 10, // 10 minutos - tempo de cache
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Manter compatibilidade: retornar error como string (se houver)
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'equipment',
        })
        : null;

    return {
        data: data || [],
        isLoading,
        isError,
        error: errorMessage,
        refetch,
    };
};
